/**
 * PostHog Analytics Integration
 * 
 * Production-ready wrapper for PostHog browser SDK with GDPR compliance.
 * 
 * Features:
 * - Explicit opt-in consent (Fixes F001)
 * - HMAC-SHA256 user ID hashing (Fixes F002)
 * - Runtime PII validation (Fixes F005)
 * - Automatic platform detection
 * - Privacy-safe configuration
 */

import posthog from 'posthog-js';
import { hashUserId, getPlatform } from '../../utils/telemetry/pii-redaction';
import { validateEventProperties } from '../../utils/telemetry/pii-guard';
import { validateEventSchema, shouldSampleEvent } from '../../utils/telemetry/event-schema';

let isInitialized = false;
let currentUserId: string | null = null;

/**
 * Get analytics consent from user settings
 * 
 * Fixes F001: Explicit opt-in required (default false)
 * 
 * @returns true if user has explicitly opted in
 */
function getAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Check localStorage for consent (backup if user_settings not available)
  const localConsent = localStorage.getItem('analytics_consent');
  if (localConsent === 'false') return false;
  if (localConsent === 'true') return true;
  
  // Default: false (explicit opt-in required)
  return false;
}

/**
 * Initialize PostHog SDK
 * 
 * Should be called after user authentication to ensure proper user identification.
 * Will NOT initialize if user has not consented (GDPR compliance).
 */
export function initializePostHog(): void {
  if (typeof window === 'undefined') {
    console.log('[PostHog] Skipping initialization (no window object)');
    return;
  }
  
  if (isInitialized) {
    console.log('[PostHog] Already initialized');
    return;
  }
  
  // Check user consent (Fixes F001: Explicit opt-in required)
  const consentGiven = getAnalyticsConsent();
  if (consentGiven !== true) {
    console.log('[PostHog] User has not opted in to analytics - initialization skipped');
    return;
  }
  
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  if (!apiKey || apiKey === 'phc_placeholder_dev' || apiKey === 'undefined' || apiKey === '') {
    if (!import.meta.env.DEV) {
      console.error('[PostHog] VITE_POSTHOG_API_KEY not configured for production');
    } else {
      console.log('[PostHog] No API key configured - running in dev mode without analytics');
    }
    return;
  }
  
  try {
    console.log('[PostHog] Initializing with API key:', apiKey.substring(0, 10) + '...');
    
    posthog.init(apiKey, {
      api_host: 'https://eu.i.posthog.com',
      
      // Privacy settings
      autocapture: false, // Manual event tracking only (privacy)
      capture_pageview: false, // Manual page tracking (Ionic navigation)
      disable_session_recording: true, // No session replay (privacy)
      
      // Performance settings
      loaded: (posthog) => {
        console.log('[PostHog] Successfully initialized');
        if (import.meta.env.DEV) {
          posthog.debug(); // Enable debug logging in dev
        }
      },
      
      // PII redaction
      sanitize_properties: (properties, event) => {
        // Remove any accidental PII from properties
        const sanitized = { ...properties };
        
        // Remove URL-related properties that may contain tokens
        delete sanitized.$current_url;
        delete sanitized.$referrer;
        delete sanitized.$pathname;
        delete sanitized.$host;
        
        return sanitized;
      },
      
      // Persistence settings
      persistence: 'localStorage',
      cross_subdomain_cookie: false,
      
      // Advanced settings
      respect_dnt: true, // Respect Do Not Track
      ip: false, // Don't track IP addresses
      advanced_disable_decide: true, // Prevent extendPostHogWithExceptionAutocapture error from /decide response
    });
    
    isInitialized = true;
    console.log('[PostHog] Initialized successfully');
  } catch (error) {
    console.error('[PostHog] Initialization failed:', error);
  }
}

/**
 * Identify user with PostHog
 * 
 * Uses HMAC-SHA256 hashed user ID for privacy (Fixes F002)
 * 
 * @param userId - Supabase user ID (will be hashed)
 * @param isAdmin - Whether user is admin (for debug event sampling)
 */
export async function identifyUser(userId: string, isAdmin: boolean = false): Promise<void> {
  if (!isInitialized || !posthog.__loaded) {
    console.log('[PostHog] Not initialized - skipping user identification');
    return;
  }
  
  try {
    // Hash user ID (Fixes F002: HMAC-SHA256 with secret salt)
    const hashedUserId = await hashUserId(userId);
    currentUserId = hashedUserId;
    
    // Identify user with hashed ID
    posthog.identify(hashedUserId, {
      platform: getPlatform(),
      is_admin: isAdmin,
    });
    
    console.log('[PostHog] User identified:', hashedUserId.substring(0, 8) + '...');
  } catch (error) {
    console.error('[PostHog] Failed to identify user:', error);
  }
}

/**
 * Track custom event
 * 
 * Validates event properties for PII before dispatch (Fixes F005)
 * Validates event schema for required fields
 * 
 * @param eventName - Event name (e.g., 'loop.start')
 * @param properties - Event properties (will be validated)
 * @param isAdmin - Whether user is admin (for sampling)
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, any>,
  isAdmin: boolean = false
): void {
  if (!isInitialized || !posthog.__loaded) {
    // Silently skip if not initialized (consent not given)
    return;
  }
  
  try {
    // Check if event should be sampled (debug events only)
    if (!shouldSampleEvent(eventName, isAdmin)) {
      if (import.meta.env.DEV) {
        console.log('[PostHog] Event sampled out:', eventName);
      }
      return;
    }
    
    // Add timestamp and platform if not provided
    const enrichedProperties = {
      platform: getPlatform(),
      timestamp: Date.now(),
      ...properties,
    };
    
    // Validate event schema
    validateEventSchema(eventName, enrichedProperties);
    
    // Validate no PII in properties (Fixes F005: Runtime PII guard)
    validateEventProperties(enrichedProperties);
    
    // Track event
    posthog.capture(eventName, enrichedProperties);
    
    if (import.meta.env.DEV) {
      console.log('[PostHog] Event tracked:', eventName, enrichedProperties);
    }
  } catch (error) {
    // Log error in development, silently fail in production
    if (import.meta.env.DEV) {
      console.error('[PostHog] Failed to track event:', eventName, error);
    }
  }
}

/**
 * Set user properties
 * 
 * Updates user profile attributes in PostHog.
 * Use sparingly - only for non-PII attributes.
 * 
 * @param properties - User properties (must not contain PII)
 */
export function setUserProperties(properties: Record<string, any>): void {
  if (!isInitialized || !posthog.__loaded) {
    return;
  }
  
  try {
    // Validate no PII in properties
    validateEventProperties(properties);
    
    posthog.people.set(properties);
    
    if (import.meta.env.DEV) {
      console.log('[PostHog] User properties set:', properties);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[PostHog] Failed to set user properties:', error);
    }
  }
}

/**
 * Shutdown PostHog
 * 
 * Called when user opts out of analytics or signs out.
 * Clears all PostHog data from localStorage.
 */
export function shutdownPostHog(): void {
  if (!isInitialized) {
    return;
  }
  
  try {
    posthog.reset();
    isInitialized = false;
    currentUserId = null;
    console.log('[PostHog] Shutdown complete');
  } catch (error) {
    console.error('[PostHog] Shutdown failed:', error);
  }
}

/**
 * Get current PostHog status
 * 
 * @returns Object with initialization and consent status
 */
export function getPostHogStatus(): {
  initialized: boolean;
  consentGiven: boolean;
  userId: string | null;
} {
  return {
    initialized: isInitialized,
    consentGiven: getAnalyticsConsent(),
    userId: currentUserId,
  };
}

/**
 * Check if PostHog is ready to track events
 * 
 * @returns true if initialized and consent given
 */
export function isPostHogReady(): boolean {
  return isInitialized && posthog.__loaded && getAnalyticsConsent();
}

/**
 * Set analytics consent and initialize/shutdown PostHog accordingly
 * 
 * Implements GDPR Article 7(3) compliance: Users can withdraw consent as easily as granting it.
 * This function enables users to toggle analytics on/off from Settings screen.
 * 
 * @param enabled - true to grant consent and initialize, false to revoke consent and shutdown
 */
export function setAnalyticsConsent(enabled: boolean): void {
  if (typeof window === 'undefined') {
    console.warn('[PostHog] Cannot set consent - window object not available');
    return;
  }
  
  // Update localStorage with consent choice
  localStorage.setItem('analytics_consent', enabled ? 'true' : 'false');
  localStorage.setItem('analytics_consent_shown', 'true');
  
  if (enabled) {
    // User granted consent - initialize PostHog
    console.log('[PostHog] Consent granted - initializing');
    initializePostHog();
  } else {
    // User revoked consent - shutdown PostHog
    console.log('[PostHog] Consent revoked - shutting down');
    shutdownPostHog();
  }
}
// ============================================================================
// Event Dispatch Functions (Epic 3.10 - v0.3.1)
// ============================================================================

/**
 * Track checkpoint visit during points loop run
 * 
 * @param loopId - Loop identifier
 * @param runId - Run identifier
 * @param checkpointId - Checkpoint identifier
 * @param sequenceNumber - Order of checkpoint visit (1-indexed)
 * @param timeSinceStart - Milliseconds since run start
 * @param totalCheckpoints - Total checkpoints in loop
 */
export function trackLoopCheckpoint(
  loopId: string,
  runId: string,
  checkpointId: string,
  sequenceNumber: number,
  timeSinceStart: number,
  totalCheckpoints: number
): void {
  trackEvent('loop.checkpoint', {
    runType: 'points',
    loopId,
    runId,
    checkpointId,
    sequenceNumber,
    timeSinceStart,
    totalCheckpoints,
  });
}

/**
 * Track event creation
 * 
 * @param eventId - Event identifier
 * @param eventType - 'public' or 'private'
 * @param loopId - Loop identifier
 * @param loopType - Loop run type
 * @param maxParticipants - Optional max participants
 * @param eventDate - Optional event date
 */
export function trackEventCreate(
  eventId: string,
  eventType: 'public' | 'private',
  loopId: string,
  loopType: 'classic' | 'points' | 'hash',
  maxParticipants?: number,
  eventDate?: string
): void {
  trackEvent('event.create', {
    eventType,
    eventId,
    loopId,
    loopType,
    ...(maxParticipants && { maxParticipants }),
    ...(eventDate && { eventDate }),
  });
}

/**
 * Track event join
 * 
 * @param eventId - Event identifier
 * @param eventType - 'public' or 'private'
 * @param joinMethod - 'browse' or 'code'
 * @param participantCount - Optional current participant count
 */
export function trackEventJoin(
  eventId: string,
  eventType: 'public' | 'private',
  joinMethod: 'browse' | 'code',
  participantCount?: number
): void {
  trackEvent('event.join', {
    eventType,
    eventId,
    joinMethod,
    ...(participantCount && { participantCount }),
  });
}

/**
 * Track event leave
 * 
 * @param eventId - Event identifier
 * @param eventType - 'public' or 'private'
 */
export function trackEventLeave(
  eventId: string,
  eventType: 'public' | 'private'
): void {
  trackEvent('event.leave', {
    eventType,
    eventId,
  });
}

/**
 * Track event completion
 * 
 * @param eventId - Event identifier
 * @param eventType - 'public' or 'private'
 * @param runId - Run identifier
 * @param placement - User's placement (optional)
 * @param totalParticipants - Total participants (optional)
 * @param completionTime - Completion time in ms (optional)
 */
export function trackEventComplete(
  eventId: string,
  eventType: 'public' | 'private',
  runId: string,
  placement?: number,
  totalParticipants?: number,
  completionTime?: number
): void {
  trackEvent('event.complete', {
    eventType,
    eventId,
    runId,
    ...(placement && { placement }),
    ...(totalParticipants && { totalParticipants }),
    ...(completionTime && { completionTime }),
  });
}

/**
 * Track user sign in
 * 
 * @param success - Whether sign in succeeded
 * @param method - 'email' or 'oauth'
 */
export function trackAuthSignIn(
  success: boolean,
  method: 'email' | 'oauth' = 'email'
): void {
  trackEvent('auth.sign_in', {
    platform: getPlatform(),
    success,
    method,
  });
}

/**
 * Track user sign out
 */
export function trackAuthSignOut(): void {
  trackEvent('auth.sign_out', {
    platform: getPlatform(),
  });
}

/**
 * Track session restoration from localStorage
 * 
 * @param success - Whether session restoration succeeded
 */
export function trackAuthSessionRestored(success: boolean): void {
  trackEvent('auth.session_restored', {
    platform: getPlatform(),
    success,
  });
}

/**
 * Track sign-up flow start
 * 
 * @param method - 'email' or 'oauth'
 * @param provider - OAuth provider if applicable
 */
export function trackAuthSignUpStart(
  method: 'email' | 'oauth',
  provider?: 'google' | 'strava'
): void {
  trackEvent('auth.sign_up_start', {
    platform: getPlatform(),
    method,
    ...(provider && { provider }),
  });
}

/**
 * Track sign-up completion
 * 
 * @param success - Whether sign-up succeeded
 * @param method - 'email' or 'oauth'
 * @param provider - OAuth provider if applicable
 */
export function trackAuthSignUpComplete(
  success: boolean,
  method: 'email' | 'oauth',
  provider?: 'google' | 'strava'
): void {
  trackEvent('auth.sign_up_complete', {
    platform: getPlatform(),
    method,
    success,
    ...(provider && { provider }),
  });
}

/**
 * Track admin loop creation
 * 
 * @param loopId - Loop identifier
 * @param loopType - 'classic' or 'points'
 * @param checkpointCount - Number of checkpoints (optional)
 */
export function trackAdminLoopCreate(
  loopId: string,
  loopType: 'classic' | 'points',
  checkpointCount?: number
): void {
  trackEvent('admin.loop.create', {
    loopType,
    loopId,
    ...(checkpointCount && { checkpointCount }),
  });
}

/**
 * Track admin loop update
 * 
 * @param loopId - Loop identifier
 * @param loopType - Loop type
 * @param changes - Array of changed fields
 */
export function trackAdminLoopUpdate(
  loopId: string,
  loopType: 'classic' | 'points' | 'hash',
  changes: string[]
): void {
  trackEvent('admin.loop.update', {
    loopType,
    loopId,
    changes,
  });
}

/**
 * Track admin loop deletion
 * 
 * @param loopId - Loop identifier
 * @param loopType - Loop type
 */
export function trackAdminLoopDelete(
  loopId: string,
  loopType: 'classic' | 'points' | 'hash'
): void {
  trackEvent('admin.loop.delete', {
    loopType,
    loopId,
  });
}

/**
 * Track admin user role change
 * 
 * @param targetUserId - Target user ID (will be hashed)
 * @param newRole - 'admin' or 'user'
 */
export function trackAdminUserSetRole(
  targetUserId: string,
  newRole: 'admin' | 'user'
): void {
  // Hash target user ID for privacy
  hashUserId(targetUserId).then(hashedUserId => {
    trackEvent('admin.user.set_role', {
      targetUserId: hashedUserId,
      newRole,
    });
  });
}

/**
 * Track admin event finalization
 * 
 * @param eventId - Event identifier
 * @param participantCount - Number of participants
 */
export function trackAdminEventFinalize(
  eventId: string,
  participantCount: number
): void {
  trackEvent('admin.event.finalize', {
    eventId,
    participantCount,
  });
}

/**
 * Track GPS diagnostics (10% sample or admin-only)
 * 
 * @param watchState - GPS watch state
 * @param accuracy - GPS accuracy in meters
 * @param distanceFilter - Distance filter setting (optional)
 * @param pointsBuffered - Number of buffered points (optional)
 * @param isAdmin - Whether user is admin (bypasses sampling)
 */
export function trackDebugGPS(
  watchState: string,
  accuracy: number,
  distanceFilter?: number,
  pointsBuffered?: number,
  isAdmin: boolean = false
): void {
  // Note: shouldSampleEvent() in trackEvent() handles 10% sampling
  trackEvent('debug.gps', {
    watchState,
    accuracy,
    ...(distanceFilter && { distanceFilter }),
    ...(pointsBuffered && { pointsBuffered }),
  }, isAdmin);
}

/**
 * Track navigation performance (10% sample or admin-only)
 * 
 * @param transitionName - Navigation transition name
 * @param durationMs - Duration in milliseconds
 * @param droppedFrames - Number of dropped frames (optional)
 * @param isAdmin - Whether user is admin (bypasses sampling)
 */
export function trackDebugNavigation(
  transitionName: string,
  durationMs: number,
  droppedFrames?: number,
  isAdmin: boolean = false
): void {
  trackEvent('debug.navigation', {
    transitionName,
    durationMs,
    ...(droppedFrames && { droppedFrames }),
  }, isAdmin);
}

/**
 * Track backend initialization diagnostics (10% sample or admin-only)
 * 
 * @param result - Initialization result
 * @param reason - Reason message
 * @param seedVersion - Seed version number (optional)
 * @param durationMs - Duration in milliseconds (optional)
 * @param isAdmin - Whether user is admin (bypasses sampling)
 */
export function trackDebugInit(
  result: string,
  reason: string,
  seedVersion?: number,
  durationMs?: number,
  isAdmin: boolean = false
): void {
  trackEvent('debug.init', {
    result,
    reason,
    ...(seedVersion && { seed_version: seedVersion }),
    ...(durationMs && { duration_ms: durationMs }),
  }, isAdmin);
}